import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchCategoryProductCounts,
  fetchProduct,
  fetchProductCount,
  fetchProductList,
  fetchRelatedProducts,
} from "@/lib/api";
import type { ProductListParams } from "@/types/product";

const PRODUCTS_STALE_TIME = 5 * 60_000;
const PRODUCTS_GC_TIME = 30 * 60_000;

export function productListQueryKey(params: ProductListParams = {}) {
  return [
    "products",
    "list",
    params.page ?? 1,
    params.limit ?? 24,
    params.category ?? "",
    params.q ?? "",
    params.sort ?? "name",
  ] as const;
}

export function useProductList(params: ProductListParams) {
  return useQuery({
    queryKey: productListQueryKey(params),
    queryFn: () => fetchProductList(params),
    staleTime: PRODUCTS_STALE_TIME,
    gcTime: PRODUCTS_GC_TIME,
    placeholderData: (previous) => previous,
  });
}

export function useProductCount() {
  return useQuery({
    queryKey: ["products", "count"] as const,
    queryFn: () => fetchProductCount(),
    staleTime: PRODUCTS_STALE_TIME,
    gcTime: PRODUCTS_GC_TIME,
  });
}

export function useCategoryProductCounts() {
  return useQuery({
    queryKey: ["products", "category-counts"] as const,
    queryFn: fetchCategoryProductCounts,
    staleTime: PRODUCTS_STALE_TIME,
    gcTime: PRODUCTS_GC_TIME,
  });
}

export function useProduct(productId?: string) {
  return useQuery({
    queryKey: ["products", "detail", productId] as const,
    queryFn: () => fetchProduct(productId!),
    enabled: Boolean(productId),
    staleTime: PRODUCTS_STALE_TIME,
    gcTime: PRODUCTS_GC_TIME,
  });
}

export function useRelatedProducts(productId?: string, limit = 4) {
  return useQuery({
    queryKey: ["products", "related", productId, limit] as const,
    queryFn: () => fetchRelatedProducts(productId!, limit),
    enabled: Boolean(productId),
    staleTime: PRODUCTS_STALE_TIME,
    gcTime: PRODUCTS_GC_TIME,
  });
}

export function useInvalidateProducts() {
  const queryClient = useQueryClient();

  return async (productId?: string) => {
    await queryClient.invalidateQueries({ queryKey: ["products"] });
    if (productId) {
      await queryClient.invalidateQueries({ queryKey: ["products", "detail", productId] });
      await queryClient.invalidateQueries({ queryKey: ["products", "related", productId] });
    }
  };
}

/** Admin listing uses the same paginated API with a higher page size. */
export function useAdminProductList(params: ProductListParams) {
  return useProductList(params);
}
